import React, { useState } from "react";
import { Image, Modal, Button, Alert, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import "./AutoexcluidosDetalle.css";

const BASE = "https://us-central1-inprojuy-f1173.cloudfunctions.net/app";

const AutoexcluidosDetalle = ({ autoexcluidoData, showModal, modalImageSrc, handleModalShow, handleModalClose }) => {
  const formatFechaHora = (marcaDeTiempo) => {
    const fecha = new Date(marcaDeTiempo);
    const opciones = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: 'UTC',
    };
    return new Intl.DateTimeFormat('es-AR', opciones).format(fecha);
  };

  const esUrl = (valor) =>
    typeof valor === 'string' && /^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/.test(valor);

  const renderValor = (valor, etiquetaLink) => {
    if (typeof valor === 'boolean') return valor ? 'Sí' : 'No';
    if (esUrl(valor)) {
      return (
        <span className="ae-link" onClick={() => handleModalShow(valor)}>
          {etiquetaLink}
        </span>
      );
    }
    return valor;
  };

  // Bloque "antes → después" (se reutiliza en tabla y en cards)
  const tieneCambioValor = (cambio) =>
    cambio.valorAnterior !== undefined || cambio.valorActual !== undefined;

  const renderCambioValor = (cambio) => {
    if (!tieneCambioValor(cambio)) return null;
    return (
      <div className="ae-change">
        {cambio.valorAnterior !== undefined && (
          <span className="ae-pill antes">{renderValor(cambio.valorAnterior, 'Ver anterior')}</span>
        )}
        {cambio.valorAnterior !== undefined && cambio.valorActual !== undefined && (
          <span className="arrow">→</span>
        )}
        {cambio.valorActual !== undefined && (
          <span className="ae-pill despues">{renderValor(cambio.valorActual, 'Ver actual')}</span>
        )}
      </div>
    );
  };

  const [loading, setLoading] = useState(false);
  const [dryRunLoading, setDryRunLoading] = useState(false);
  const [feedback, setFeedback] = useState(null); // { type, message, detail? }

  const auth = useAuth();

  const registrarIntento = async (dryRun = false) => {
    if (!auth.user || !autoexcluidoData?.id) return;

    const setSetter = dryRun ? setDryRunLoading : setLoading;
    setSetter(true);
    setFeedback(null);

    try {
      const { data } = await axios.post(`${BASE}/registrar-intento-ingreso`, {
        autoexcluidoId: autoexcluidoData.id,
        dryRun,
      });

      if (dryRun) {
        const mailDestinosStr = Array.isArray(data.mailEnviadoA)
          ? data.mailEnviadoA.join(', ')
          : data.mailEnviadoA;
        setFeedback({
          type: 'info',
          message: `[Prueba] Mail de prueba enviado a: ${mailDestinosStr}. En el flujo real se notificaría a ${data.destinatarios.length} destinatario(s):`,
          detail: data.destinatarios.join(', '),
        });
      } else {
        setFeedback({
          type: 'success',
          message: `Intento registrado correctamente. Notificaciones enviadas a ${data.correosEnviados.length} destinatario(s).`,
        });
      }
    } catch (error) {
      const msg = error.response?.data?.message || error.message || "Error desconocido";
      setFeedback({ type: 'danger', message: `Error: ${msg}` });
    } finally {
      setSetter(false);
    }
  };

  const fotoPrincipal = autoexcluidoData
    ? (autoexcluidoData.fotos?.filter((f) => f !== null).pop() || '')
    : '';

  const infoRows = autoexcluidoData ? [
    ['Email', autoexcluidoData.mail],
    ['Domicilio', autoexcluidoData.domicilio],
    ['Nacionalidad', autoexcluidoData.nacionalidad],
    ['Teléfono', autoexcluidoData.telefono],
    ['Teléfono de apoyo', autoexcluidoData.telefonoApoyo],
    ['Ocupación', autoexcluidoData.ocupacion],
    ['Localidad', autoexcluidoData.localidad],
    ['Fecha de nacimiento', autoexcluidoData.fechaNacimiento],
    ['Fecha de alta', autoexcluidoData.alta?.split('T')[0]],
    ['Fecha de vencimiento', autoexcluidoData.vencimiento?.split('T')[0]],
    ['Lugar de entrega', autoexcluidoData.lugarEntrega],
  ].filter(([, v]) => v !== undefined && v !== null && v !== '') : [];

  return (
    <div className="ae-detalle fondotransparente5">
      <div className="ae-card">
        <div className="ae-card-head">
          <h2>Detalle del autoexcluido</h2>
        </div>

        {autoexcluidoData && (
          <div className="ae-body">

            {/* Identidad */}
            <div className="ae-ident">
              {fotoPrincipal ? (
                <img
                  className="ae-foto"
                  src={fotoPrincipal}
                  alt={`Foto de ${autoexcluidoData.nombre}`}
                  onClick={() => handleModalShow(fotoPrincipal)}
                />
              ) : (
                <div className="ae-foto" />
              )}
              <div className="ae-ident-info">
                <p className="ae-nombre">{autoexcluidoData.nombre}</p>
                <p className="ae-dni">DNI {autoexcluidoData.dni}</p>
                <span className={`ae-badge ${autoexcluidoData.activo ? 'is-activo' : 'is-inactivo'}`}>
                  {autoexcluidoData.activo ? 'Activo' : 'Inactivo'}
                </span>
              </div>
            </div>

            {/* Datos */}
            {infoRows.length > 0 && (
              <div className="ae-info">
                {infoRows.map(([k, v]) => (
                  <div className="ae-info-row" key={k}>
                    <span className="k">{k}</span>
                    <span className="v">{v}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Documentos */}
            {autoexcluidoData.pdfDocument && autoexcluidoData.pdfDocument.length > 0 && (
              <div className="ae-section">
                <p className="ae-section-title">Documentos adjuntos</p>
                {autoexcluidoData.pdfDocument.map((doc, i) => (
                  <div className="ae-doc" key={i}>
                    <span>{doc.nombredoc}</span>
                    <a href={doc.url} target="_blank" rel="noopener noreferrer">Ver PDF</a>
                  </div>
                ))}
              </div>
            )}

            {/* Observaciones */}
            {autoexcluidoData.observaciones && autoexcluidoData.observaciones.length > 0 && (
              <div className="ae-section">
                <p className="ae-section-title">Observaciones</p>
                {autoexcluidoData.observaciones.map((obs, i) => (
                  <div className="ae-hist-card" key={i}>
                    <div className="ae-hist-card-top">
                      <span className="fecha">{obs.fecha?.split('T')[0]}</span>
                    </div>
                    <p className="usuario" style={{ color: '#1f2933', marginTop: 4 }}>
                      {obs.observacion || <em style={{ color: '#98a2b3' }}>Sin texto</em>}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {/* Historial de cambios */}
            {autoexcluidoData.cambios && autoexcluidoData.cambios.length > 0 && (
              <div className="ae-section">
                <p className="ae-section-title">Historial de cambios</p>

                {/* Desktop: tabla */}
                <table className="ae-hist-table">
                  <thead>
                    <tr>
                      <th>Tipo de cambio</th>
                      <th>Fecha y hora</th>
                      <th>Usuario</th>
                      <th>Valor anterior</th>
                      <th>Valor actual</th>
                    </tr>
                  </thead>
                  <tbody>
                    {autoexcluidoData.cambios.map((cambio, i) => (
                      <tr key={i}>
                        <td>{cambio.tipo}</td>
                        <td>{formatFechaHora(cambio.marcaDeTiempo)}</td>
                        <td>{cambio.usuario}</td>
                        <td>{cambio.valorAnterior !== undefined ? renderValor(cambio.valorAnterior, 'Ver anterior') : ''}</td>
                        <td>{cambio.valorActual !== undefined ? renderValor(cambio.valorActual, 'Ver actual') : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Mobile: cards */}
                <div className="ae-hist-cards">
                  {autoexcluidoData.cambios.map((cambio, i) => (
                    <div className="ae-hist-card" key={i}>
                      <div className="ae-hist-card-top">
                        <span className="tipo">{cambio.tipo}</span>
                        <span className="fecha">{formatFechaHora(cambio.marcaDeTiempo)}</span>
                      </div>
                      {cambio.usuario && <p className="usuario">{cambio.usuario}</p>}
                      {renderCambioValor(cambio)}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Acciones */}
            <div className="ae-actions">
              {feedback && (
                <Alert
                  variant={feedback.type}
                  onClose={() => setFeedback(null)}
                  dismissible
                  className="ae-feedback"
                  style={{ fontSize: 14, marginBottom: 0 }}
                >
                  <div>{feedback.message}</div>
                  {feedback.detail && (
                    <div style={{ fontSize: 12, marginTop: 6, color: '#555', wordBreak: 'break-all' }}>
                      {feedback.detail}
                    </div>
                  )}
                </Alert>
              )}

              <button
                className="ae-btn ae-btn-primary"
                onClick={() => registrarIntento(false)}
                disabled={loading || dryRunLoading}
              >
                {loading ? <Spinner animation="border" size="sm" /> : 'Registrar asistencia a agencia o sala de juego'}
              </button>

              <Link className="ae-btn ae-btn-outline" to={`/ModificarAutoexcluido/${autoexcluidoData.dni}`}>
                Editar
              </Link>

              {auth.esSuperadmin && (
                <button
                  className="ae-btn ae-btn-ghost"
                  onClick={() => registrarIntento(true)}
                  disabled={loading || dryRunLoading}
                >
                  {dryRunLoading ? <Spinner animation="border" size="sm" /> : 'Probar (sin enviar)'}
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modal de imagen ampliada */}
      <Modal show={showModal} onHide={handleModalClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Imagen</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Image src={modalImageSrc} alt="Imagen" fluid />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleModalClose}>Cerrar</Button>
        </Modal.Footer>
      </Modal>

      {/* Barra fija inferior */}
      <div className="ae-bottombar">
        <button className="ae-btn" onClick={handleModalClose}>Cerrar</button>
      </div>
    </div>
  );
};

export default AutoexcluidosDetalle;
